/**
 * Typed client for the PUBLIC half of klapzo-kit-api — the global method library (task 2.2/2.3).
 *
 * `/public/*` is unauthenticated by design: /kit and /kit/m/:slug are crawler-facing pages, and the
 * authenticated `/methods` routes would 401 every anonymous visitor. Credentials are omitted
 * explicitly so the session cookie never rides along to a route whose responses are cacheable.
 *
 * Tenant-authored methods are NOT served here; they live behind the authenticated routes.
 */
import * as React from "react";
import type { Method, KitLocale, RelatedMethod } from "@klapzo/kit-core";

const KIT_API_BASE = (import.meta.env.VITE_KIT_API_BASE as string | undefined) ?? "https://kit-api.klapzo.app";

async function getJson<T>(path: string, signal: AbortSignal): Promise<T> {
  const res = await fetch(`${KIT_API_BASE}${path}`, { credentials: "omit", signal });
  if (!res.ok) throw new Error(`kit-api ${path} -> ${res.status}`);
  return (await res.json()) as T;
}

export async function listMethods(locale: KitLocale, signal: AbortSignal): Promise<Method[]> {
  const { methods } = await getJson<{ methods: Method[] }>(`/public/methods?locale=${locale}`, signal);
  return methods;
}

export interface MethodPage {
  method: Method;
  /** Locales this slug actually exists in — the input to hreflang alternates. */
  locales: KitLocale[];
  /** Up to four methods sharing tags, ranked server-side. Empty when nothing overlaps. */
  related: RelatedMethod[];
}

/** null = 404 (unknown slug), which the detail screen renders as a not-found page, not an error. */
export async function getMethod(slug: string, locale: KitLocale, signal: AbortSignal): Promise<MethodPage | null> {
  const res = await fetch(`${KIT_API_BASE}/public/methods/${encodeURIComponent(slug)}?locale=${locale}`, {
    credentials: "omit",
    signal,
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`kit-api /public/methods/${slug} -> ${res.status}`);
  return (await res.json()) as MethodPage;
}

export type Remote<T> = { state: "loading" } | { state: "error" } | { state: "ready"; data: T };

/**
 * The library used to be a bundled constant, so it could not fail or be pending. Now it can be
 * both, and every screen reading it has to say so — hence one hook rather than the same
 * loading/error/abort block copied into each screen.
 */
export function useRemote<T>(load: (signal: AbortSignal) => Promise<T>, deps: readonly unknown[]): Remote<T> {
  const [remote, setRemote] = React.useState<Remote<T>>({ state: "loading" });
  React.useEffect(() => {
    const controller = new AbortController();
    setRemote({ state: "loading" });
    load(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setRemote({ state: "ready", data });
      },
      (err: unknown) => {
        if (controller.signal.aborted) return;
        console.error(err);
        setRemote({ state: "error" });
      },
    );
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return remote;
}
